import React from "react";
import { DateTime } from "luxon";
import useRepurchaseSimulator from "./useRepurchaseSimulator";
import SelectSearch from "../../../common/SelectSearch";

const formatDate = (iso) => {
  if (!iso) return "—";
  return DateTime.fromISO(iso).toFormat("dd/MM/yyyy");
};

const formatPeriod = (validFrom, validTo) => {
  const from = formatDate(validFrom);
  return validTo ? `du ${from} au ${formatDate(validTo)}` : `depuis le ${from}`;
};

const RepurchaseSimulator = ({ initialValues = {} }) => {
  const {
    form,
    result,
    error,
    isLoading,
    brands,
    brandModels,
    allBrandModels,
    carburants,
    modesResolution,
    canSubmit,
    handleChange,
    submit,
    reset,
  } = useRepurchaseSimulator(initialValues);

  const onSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const resolvedMode =
    result && result.mode_resolution
      ? modesResolution.find((m) => m.id === result.mode_resolution) || {
          id: result.mode_resolution,
          name: result.mode_resolution,
        }
      : null;

  return (
    <form onSubmit={onSubmit}>
      <div className="row">
        <div className="col-12 col-md-4">
          <SelectSearch
            name="brandUuid"
            label="Marque"
            datas={brands}
            dataIndex="uuid"
            dataLabel="name"
            value={form.brandUuid}
            placeholder="Rechercher une marque..."
            change={(e) => handleChange("brandUuid", e.target.value)}
          />
        </div>
        <div className="col-12 col-md-4">
          <SelectSearch
            name="brandModelUuid"
            label="Modèle"
            datas={form.brandUuid ? brandModels : allBrandModels}
            dataIndex="uuid"
            dataLabel="name"
            dataLabel2="brand.name"
            value={form.brandModelUuid}
            placeholder="Rechercher un modèle..."
            change={(e) => handleChange("brandModelUuid", e.target.value)}
          />
        </div>
        <div className="col-12 col-md-4 form-group">
          <label htmlFor="rsim-fuel">Carburant</label>
          <select
            id="rsim-fuel"
            className="form-control form-control-sm"
            value={form.carburantId}
            onChange={(e) => handleChange("carburantId", e.target.value)}
          >
            <option value="">...</option>
            {carburants.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 form-group">
          <label htmlFor="rsim-date">Date</label>
          <input
            id="rsim-date"
            type="date"
            className="form-control form-control-sm"
            value={form.date}
            onChange={(e) => handleChange("date", e.target.value)}
          />
        </div>
        <div className="col-12 col-md-4 form-group">
          <label htmlFor="rsim-duree">Durée (mois)</label>
          <input
            id="rsim-duree"
            type="number"
            min="1"
            className="form-control form-control-sm"
            value={form.duree}
            onChange={(e) => handleChange("duree", e.target.value)}
            placeholder="Optionnel"
          />
        </div>
        <div className="col-12 col-md-4 form-group">
          <label htmlFor="rsim-km">Kilométrage</label>
          <input
            id="rsim-km"
            type="number"
            min="0"
            className="form-control form-control-sm"
            value={form.kilometrage}
            onChange={(e) => handleChange("kilometrage", e.target.value)}
            placeholder="Optionnel"
          />
        </div>
      </div>

      <div className="d-flex justify-content-end" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={reset}
        >
          <i className="fa fa-times mr-1" /> Réinitialiser
        </button>
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={!canSubmit || isLoading}
        >
          {isLoading ? (
            <>
              <i className="fa fa-spinner fa-spin mr-1" /> Recherche...
            </>
          ) : (
            <>
              <i className="fa fa-search mr-1" /> Trouver la grille
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-warning mt-3 mb-0 py-2">
          <i className="fa fa-exclamation-triangle mr-2" />
          {error}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 border rounded bg-light">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div className="font-weight-bold">{result.name}</div>
              <small className="text-muted">
                Validité {formatPeriod(result.valid_from, result.valid_to)}
              </small>
            </div>
            <a
              href={`/repurchases/${result.uuid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-default d-flex align-items-center bg-white"
            >
              <i className="fa fa-external-link-alt mr-1" /> Ouvrir
            </a>
          </div>

          {resolvedMode && (
            <div className="mb-2">
              <span className="badge badge-info">
                Mode : {resolvedMode.name}
              </span>
            </div>
          )}

          {result.taux_resolu !== undefined &&
            result.taux_resolu !== null &&
            result.taux_resolu !== "" && (
              <div className="mt-2">
                <span className="text-muted">Taux résolu : </span>
                <span className="h5 mb-0">
                  {(Number(result.taux_resolu) * 100).toFixed(2)} %
                </span>
              </div>
            )}
        </div>
      )}
    </form>
  );
};

export default RepurchaseSimulator;
